import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useJWTAuth } from '../components/JWTAuthContext';
import { API_BASE_URL, getUserSubscriptionFeatures } from '../components/apiClient';
import '../styles/SubscriptionPlans.css';

interface Feature {
  feature_id: string;
  feature_key: string;
  feature_name: string;
  feature_description: string;
  feature_type: 'quota' | 'boolean' | 'credits';
  reset_cycle: string;
  feature_limit: string | null;
  is_enabled: boolean;
}

interface Plan {
  id: string;
  plan_key: string;
  plan_name: string;
  plan_description: string;
  monthly_price_inr: string;
  academic_session_price_inr: string;
  is_free: boolean;
  is_default: boolean;
  plan_color: string;
  is_popular?: boolean;
  features: Feature[];
}

interface SubscriptionPlansResponse {
  status: string;
  result: Plan[];
  message: string;
}

interface UserSubscription {
  subscription_plan_name: string;
  subscription_plan_key: string;
  subscription_status: string;
  subscription_expiry_date?: string;
}

const SubscriptionPlans: React.FC = () => {
  const { isAuthenticated, user } = useJWTAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'academic'>('monthly');
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const [userSubscription, setUserSubscription] = useState<UserSubscription | null>(null);

  const togglePlanFeatures = (planId: string) => {
    setExpandedPlans(prev => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch plans first
      const plansResponse = await fetch(`${API_BASE_URL}/subscription/plans`);
      const plansData: SubscriptionPlansResponse = await plansResponse.json();
      
      if (plansData.status === 'success') {
        // Mark Premium plan as popular (best seller)
        const updatedPlans = plansData.result.map(plan => ({
          ...plan,
          is_popular: plan.plan_key === 'premium'
        }));
        setPlans(updatedPlans);
      } else {
        setError('Failed to load subscription plans');
        setLoading(false);
        return;
      }

      // If user is authenticated, fetch their subscription
      if (isAuthenticated) {
        try {
          const subscriptionResponse = await getUserSubscriptionFeatures();
          
          // API returns status as "success" string, not boolean
          // Data is in 'result' field
          if (subscriptionResponse && 
              (subscriptionResponse.status === 'success' || subscriptionResponse.status === true) && 
              subscriptionResponse.result) {
            const userSub = {
              subscription_plan_name: subscriptionResponse.result.subscription_plan_name,
              subscription_plan_key: subscriptionResponse.result.subscription_plan_key,
              subscription_status: subscriptionResponse.result.subscription_status,
              subscription_expiry_date: subscriptionResponse.result.expires_at
            };
            setUserSubscription(userSub);
          }
        } catch (err) {
          console.error('Error fetching user subscription:', err);
          // Don't show error, just fail silently and show plans without highlighting
        }
      }
      
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load subscription plans. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const isCurrentPlan = (planKey: string): boolean => {
    // Only show current plan if user is authenticated and has subscription data
    if (!isAuthenticated || !userSubscription) return false;
    return userSubscription.subscription_plan_key === planKey;
  };

  const getPlanHierarchyLevel = (planKey: string): number => {
    const hierarchy: { [key: string]: number } = {
      'free': 0,
      'basic': 1,
      'premium': 2,
      'pro': 3
    };
    return hierarchy[planKey.toLowerCase()] ?? 0;
  };

  const isLowerThanCurrentPlan = (planKey: string): boolean => {
    // Only evaluate plan hierarchy if user is authenticated
    if (!isAuthenticated || !userSubscription) return false;
    
    const currentPlanLevel = getPlanHierarchyLevel(userSubscription.subscription_plan_key);
    const thisPlanLevel = getPlanHierarchyLevel(planKey);
    
    // Don't disable free plan
    if (planKey.toLowerCase() === 'free') return false;
    
    return thisPlanLevel < currentPlanLevel;
  };

  const formatPrice = (price: string): string => {
    const numPrice = parseFloat(price);
    if (numPrice === 0) return 'Free';
    return `₹${numPrice.toLocaleString('en-IN')}`;
  };

  const calculateSavingsPercentage = (plan: Plan): number => {
    if (plan.is_free) return 0;
    
    const monthlyPrice = parseFloat(plan.monthly_price_inr);
    const academicPrice = parseFloat(plan.academic_session_price_inr);
    
    // Assuming academic session is 12 months
    const monthlyYearlyTotal = monthlyPrice * 12;
    
    if (monthlyYearlyTotal === 0) return 0;
    
    const savings = monthlyYearlyTotal - academicPrice;
    const savingsPercentage = (savings / monthlyYearlyTotal) * 100;
    
    return Math.round(savingsPercentage);
  };

  const getFeatureIcon = (featureKey: string): string => {
    const icons: { [key: string]: string } = {
      custom_quiz: 'fa-edit',
      quiz_solutions: 'fa-lightbulb',
      report_card: 'fa-chart-bar',
      performance_analysis: 'fa-brain',
      pyqs: 'fa-history',
      diagnostic_test: 'fa-stethoscope',
      study_plan: 'fa-calendar-check',
      ai_mentor: 'fa-robot',
      mock_tests: 'fa-clipboard-check',
      score_predictor: 'fa-chart-line'
    };
    return icons[featureKey] || 'fa-check';
  };

  const getFeatureDisplay = (feature: Feature): string => {
    if (!feature.is_enabled) return '';
    
    if (feature.feature_type === 'boolean') {
      return feature.feature_name;
    }
    
    if (feature.feature_limit === null) {
      return `${feature.feature_name} - Unlimited`;
    }
    
    if (feature.feature_type === 'quota') {
      return `${feature.feature_limit} ${feature.feature_name}`;
    }
    
    if (feature.feature_type === 'credits') {
      return `${feature.feature_limit} ${feature.feature_name} Credits`;
    }
    
    return feature.feature_name;
  };

  const handlePlanAction = (plan: Plan) => {
    if (!isAuthenticated) {
      // Redirect to signup/login with return URL
      window.location.href = '/signup';
      return;
    }
    
    // Don't redirect for free plan
    if (plan.is_free) {
      alert('You are already on the free plan!');
      return;
    }
    
    // Redirect to payment page with plan details
    navigate('/payment', { 
      state: { 
        plan: plan,
        billingCycle: billingCycle 
      } 
    });
  };

  if (loading) {
    return (
      <div className="subscription-plans-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-plans-container">
        <div className="alert alert-danger m-5" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-plans-container">
      {/* Hero Section */}
      <section className="pricing-hero">
        <div className="container text-center">
          <h1 className="display-4 fw-bold mb-3">Choose Your Learning Plan</h1>
          <p className="lead text-white mb-4">
            Unlock your potential with our AI-powered learning platform
          </p>
          
          {/* Billing Cycle Toggle */}
          <div className="billing-toggle mb-5">
            <button
              className={`btn ${billingCycle === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`btn ${billingCycle === 'academic' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setBillingCycle('academic')}
            >
              Academic Session
              <span className="badge bg-success ms-2">Save More</span>
            </button>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="pricing-plans py-5">
        <div className="container">
          <div className="row g-4">
            {plans.map((plan) => {
              const isUserCurrentPlan = isCurrentPlan(plan.plan_key);
              const isLowerPlan = isLowerThanCurrentPlan(plan.plan_key);
              const isPlanDisabled = isUserCurrentPlan || isLowerPlan;
              
              return (
              <div key={plan.id} className="col-lg-3 col-md-6">
                <div 
                  className={`plan-card ${plan.is_free ? 'free-plan' : ''} ${plan.is_popular ? 'popular-plan' : ''} ${isUserCurrentPlan ? 'current-plan' : ''} ${isLowerPlan ? 'lower-plan' : ''} h-100`}
                  style={{ 
                    borderColor: plan.plan_color,
                    borderWidth: isUserCurrentPlan ? '3px' : '1px',
                    backgroundColor: isUserCurrentPlan ? `${plan.plan_color}50` : (isLowerPlan ? '#f8f9fa' : 'white'),
                    boxShadow: isUserCurrentPlan ? `0 0 20px ${plan.plan_color}FF` : undefined,
                    opacity: isLowerPlan ? 0.6 : 1
                  }}
                >
                  {/* Current Plan Badge */}
                  {isUserCurrentPlan && (
                    <div className="current-plan-badge" style={{ backgroundColor: plan.plan_color }}>
                      <i className="fas fa-check-circle me-1"></i>
                      Current Plan
                    </div>
                  )}
                  
                  {/* Popular Badge */}
                  {plan.is_popular && !isUserCurrentPlan && (
                    <div className="popular-badge" style={{ backgroundColor: plan.plan_color }}>
                      Best Seller
                    </div>
                  )}

                  {/* Plan Header */}
                  <div className="plan-header text-center" style={{ backgroundColor: `${plan.plan_color}15` }}>
                    <h3 className="plan-name fw-bold" style={{ color: plan.plan_color }}>
                      {plan.plan_name}
                    </h3>
                    <p className="plan-description text-muted">
                      {plan.plan_description}
                    </p>
                    <div className="plan-price">
                      <span className="price-amount">
                        {billingCycle === 'monthly' 
                          ? formatPrice(plan.monthly_price_inr)
                          : formatPrice(plan.academic_session_price_inr)
                        }
                      </span>
                      {!plan.is_free && (
                        <span className="price-period">
                          /{billingCycle === 'monthly' ? 'month' : 'academic session'}
                        </span>
                      )}
                      {/* Savings Badge for Academic Session */}
                      {billingCycle === 'academic' && !plan.is_free && calculateSavingsPercentage(plan) > 0 && (
                        <div className="savings-badge mt-2">
                          <span className="badge bg-success">
                            <i className="fas fa-tags me-1"></i>
                            Save {calculateSavingsPercentage(plan)}%
                          </span>
                          <div className="text-muted small mt-1">
                            vs monthly (₹{(parseFloat(plan.monthly_price_inr) * 12).toLocaleString('en-IN')}/year)
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Plan Action */}
                  <div className="plan-action">
                    {isAuthenticated && user ? (
                      <button
                        className="btn btn-plan w-100"
                        style={{
                          backgroundColor: isPlanDisabled ? '#6c757d' : plan.plan_color,
                          borderColor: isPlanDisabled ? '#6c757d' : plan.plan_color,
                          color: 'white',
                          cursor: isPlanDisabled ? 'not-allowed' : 'pointer',
                          opacity: isPlanDisabled ? 0.8 : 1
                        }}
                        onClick={() => !isPlanDisabled && handlePlanAction(plan)}
                        disabled={isPlanDisabled}
                      >
                        {isUserCurrentPlan ? (
                          <>
                            <i className="fas fa-check-circle me-2"></i>
                            Current Plan
                          </>
                        ) : isLowerPlan ? (
                          <>
                            <i className="fas fa-lock me-2"></i>
                            {plan.is_free ? 'Get Started' : 'Upgrade Now'}
                          </>
                        ) : (
                          plan.is_free ? 'Get Started' : 'Upgrade Now'
                        )}
                      </button>
                    ) : (
                      <Link
                        to="/signup"
                        className="btn btn-plan w-100"
                        style={{
                          backgroundColor: plan.plan_color,
                          borderColor: plan.plan_color,
                          color: 'white'
                        }}
                      >
                        Get Started
                      </Link>
                    )}
                  </div>

                  {/* Plan Features */}
                  <div className="plan-features">
                    {/* Mobile Toggle Button */}
                    <button 
                      className="features-toggle-btn d-md-none w-100"
                      onClick={() => togglePlanFeatures(plan.id)}
                      style={{ 
                        borderColor: plan.plan_color,
                        color: plan.plan_color 
                      }}
                    >
                      <span>
                        {expandedPlans.has(plan.id) ? 'Hide' : 'View'} Features
                      </span>
                      <i className={`fas fa-chevron-${expandedPlans.has(plan.id) ? 'up' : 'down'} ms-2`}></i>
                    </button>
                    
                    {/* Features List */}
                    <ul className={`features-list ${expandedPlans.has(plan.id) ? 'expanded' : ''}`}>
                      {plan.features.map((feature) => (
                        feature.is_enabled && (
                          <li key={feature.feature_id} className="feature-item">
                            <i className={`fas ${getFeatureIcon(feature.feature_key)} feature-icon`}
                               style={{ color: plan.plan_color }}></i>
                            <span className="feature-text">
                              {getFeatureDisplay(feature)}
                            </span>
                            {feature.feature_limit === null && feature.feature_type !== 'boolean' && (
                              <span className="unlimited-badge">
                                <i className="fas fa-infinity"></i>
                              </span>
                            )}
                          </li>
                        )
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing-faq py-5 bg-light">
        <div className="container">
          <h2 className="text-center mb-5">Frequently Asked Questions</h2>
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="faq-item">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-question-circle text-primary me-2"></i>
                  Can I change my plan later?
                </h5>
                <p className="text-muted">
                  Yes! You can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.
                </p>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div className="faq-item">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-question-circle text-primary me-2"></i>
                  What payment methods do you accept?
                </h5>
                <p className="text-muted">
                  We accept all major credit cards, debit cards, UPI, and net banking through our secure payment gateway.
                </p>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div className="faq-item">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-question-circle text-primary me-2"></i>
                  Is there a free trial?
                </h5>
                <p className="text-muted">
                  Yes! Our FREE plan gives you access to basic features. You can upgrade anytime to access premium features.
                </p>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div className="faq-item">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-question-circle text-primary me-2"></i>
                  What happens when I reach my quota limit?
                </h5>
                <p className="text-muted">
                  You can either wait for the next reset cycle or upgrade to a higher plan for more quota or unlimited access.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {!isAuthenticated && (
        <section className="pricing-cta py-5 text-center">
          <div className="container">
            <h2 className="mb-4">Ready to Start Learning?</h2>
            <p className="lead text-muted mb-4">
              Join thousands of students already learning with WiziAI
            </p>
            <div>
              <Link to="/signup" className="btn btn-primary btn-lg me-3">
                <i className="fas fa-rocket me-2"></i>
                Get Started Free
              </Link>
              <Link to="/contact" className="btn btn-outline-primary btn-lg">
                <i className="fas fa-envelope me-2"></i>
                Contact Sales
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SubscriptionPlans;
